import React, { useEffect, useState } from "react";
import { listPlatformUserSsmRevisions, downloadPlatformDocumentRevision } from "../services/api";

function mapStatusLabel(s) {
  if (!s) return "—";
  const m = {
    generated: "Generat",
    sent_for_signature: "Trimis la semnare",
    signed: "Semnat",
    failed: "Eșuat",
    cancelled: "Anulat",
  };
  return m[s] || s;
}

export default function SsmRevisions({ userId }) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [err, setErr] = useState("");
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    let alive = true;
    async function fetchList() {
      setLoading(true);
      setErr("");
      try {
        const data = await listPlatformUserSsmRevisions(userId);
        if (!alive) return;
        setItems(data.items || []);
      } catch (e) {
        if (!alive) return;
        setErr(e?.message || "Eroare la încărcare revizii SSM");
      } finally {
        if (alive) setLoading(false);
      }
    }
    if (userId) fetchList();

    function onCreated(ev) {
      try {
        const uid = ev?.detail?.userId;
        if (!uid || String(uid) !== String(userId)) return;
      } catch {
        // ignore
      }
      fetchList();
    }

    window.addEventListener("ssmRevisionCreated", onCreated);
    return () => {
      alive = false;
      window.removeEventListener("ssmRevisionCreated", onCreated);
    };
  }, [userId]);

  async function handleDownload(revId, variant = "unsigned") {
    try {
      setDownloading(revId);
      const { blob, filename } = await downloadPlatformDocumentRevision(revId, variant);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || `ssm_${revId}_${variant}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      alert(e?.message || "Eroare la descărcare");
    } finally {
      setDownloading(null);
    }
  }

  if (loading) return <div className="text-sm text-slate-500 py-3">Se încarcă…</div>;
  if (err) return <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">{err}</div>;

  if (!items || items.length === 0) {
    return <div className="text-sm text-slate-500 py-3">Nu există fișe SSM generate pentru acest utilizator.</div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-left border-b border-slate-200 text-xs text-slate-500 uppercase tracking-wide">
            <th className="py-2 pr-4">Revizie</th>
            <th className="py-2 pr-4">Status</th>
            <th className="py-2 pr-4">Slot</th>
            <th className="py-2 pr-4">Generat la</th>
            <th className="py-2 pr-4">Semnat la</th>
            <th className="py-2 pr-4">Acțiuni</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {items.map((r) => (
            <tr key={r.id} className="hover:bg-slate-50">
              <td className="py-2.5 pr-4 font-medium text-slate-900">{r.revision_number ?? r.id}</td>
              <td className="py-2.5 pr-4 text-slate-700">{mapStatusLabel(r.status)}</td>
              <td className="py-2.5 pr-4 text-slate-700">{r.route_declaration_slot ?? r.route_declaration_id ?? "—"}</td>
              <td className="py-2.5 pr-4 text-slate-500">{r.generated_at ? new Date(r.generated_at).toLocaleString() : "—"}</td>
              <td className="py-2.5 pr-4 text-slate-500">{r.signed_at ? new Date(r.signed_at).toLocaleString() : "—"}</td>
              <td className="py-2.5">
                <div className="flex gap-2">
                  <button
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-700 text-white hover:bg-blue-800 shadow-sm transition-colors ${!r.has_unsigned_pdf ? 'opacity-60 cursor-not-allowed' : ''}`}
                    onClick={() => r.has_unsigned_pdf && handleDownload(r.id, 'unsigned')}
                    disabled={!r.has_unsigned_pdf || downloading === r.id}
                  >
                    {downloading === r.id ? 'Se descarcă...' : 'Descarcă PDF generat'}
                  </button>

                  {r.has_signed_pdf && (
                    <button
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-800 text-white hover:bg-slate-900 shadow-sm transition-colors`}
                      onClick={() => handleDownload(r.id, 'signed')}
                      disabled={downloading === r.id}
                    >
                      {downloading === r.id ? 'Se descarcă...' : 'Descarcă PDF semnat'}
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
